/**
 * @file DesignCard.tsx
 * @description Карточка сохранённого дизайна (phase19.16 Р1-6а;
 * uiux-builder-shell.md §9; решения 2026-10-12): образец «Аа» и пять цветов,
 * название, кто и когда сохранил, метки «В работе» и «На сайте» одной
 * строкой; [Посмотреть] [Применить ко всему сайту] («Уже в черновике»,
 * если совпадает) и «⋯» (Обновить из текущего вида · Переименовать ·
 * Удалить). Почему пункт выключен — подписью в меню; почему нельзя удалить —
 * текстом под карточкой. Без права настроек — карточка без кнопок.
 * `flash` — подсветка 2 с после создания, применения или обновления.
 */

import { Pencil, RefreshCw, Trash2 } from "lucide-react";

import { Button } from "../../ui/button";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { StatusPill } from "../../ui/StatusPill";
import type { SiteDesignItem } from "../../../lib/api-site-designs";
import { formatEditMoment } from "../../../lib/site-view/changes";
import { designFontsText, updateActionLabel, updateBlockedText } from "../../../lib/site-view/design-status";
import { cn } from "@/lib/utils";
import { DesignColorDots, DesignFontTile } from "./DesignSwatches";

/** Почему нельзя удалить — реестром. */
const DELETE_BLOCKER_TEXT: Readonly<Record<string, string>> = {
  published: "Нельзя удалить: этот дизайн сейчас на сайте.",
  pages: "Нельзя удалить: дизайн назначен страницам.",
};

interface Props {
  item: SiteDesignItem;
  canEdit: boolean;
  /** В черновике есть логотипы — дизайн без них можно дополнить. */
  draftHasLogos?: boolean;
  flash?: boolean;
  onPreview: () => void;
  onApply: () => void;
  onUpdate: () => void;
  onRename: () => void;
  onDelete: () => void;
}

export function DesignCard({ item, canEdit, draftHasLogos = false, flash = false, onPreview, onApply, onUpdate, onRename, onDelete }: Props) {
  const blocker = item.deleteBlockers.map((code) => DELETE_BLOCKER_TEXT[code]).find(Boolean) ?? null;
  const updateHint = updateBlockedText(item, draftHasLogos);

  return (
    <article
      id={`design-${item.id}`}
      data-flash={flash ? "" : undefined}
      aria-label={`Дизайн «${item.name}»`}
      className={cn(
        "flex min-w-0 flex-col gap-3 rounded-xl bg-surface-lowest p-4 transition-[box-shadow] duration-500",
        flash ? "[box-shadow:inset_0_0_0_2px_var(--primary)]" : "[box-shadow:inset_0_0_0_1px_var(--border)]",
      )}
    >
      <div className="flex items-center gap-3">
        <DesignFontTile summary={item.summary} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="type-card-title truncate text-on-surface" title={item.name}>
            {item.name}
          </span>
          {/* На узкой панели — в несколько строк, дата не обрезается; у «Как было» автор — подпись системы, без «Сохранил». */}
          <span className="type-caption break-words text-on-surface-variant">
            {item.source === "SAVED" ? "Сохранил " : ""}
            {item.createdByName ?? "сотрудник"} · {formatEditMoment(item.updatedAt)}
          </span>
        </div>
        {canEdit ? (
          <RowActionsMenu
            title={`Дизайн «${item.name}»`}
            ariaLabel={`Действия с дизайном «${item.name}»`}
            size="compact"
            actions={[
              { key: "update", label: updateActionLabel(item, draftHasLogos), icon: RefreshCw, disabled: updateHint !== undefined, hint: updateHint, run: onUpdate },
              { key: "rename", label: "Переименовать", icon: Pencil, run: onRename },
              { key: "delete", label: "Удалить", icon: Trash2, danger: true, disabled: !item.canDelete, run: onDelete },
            ]}
          />
        ) : null}
      </div>
      <div className="flex min-w-0 items-center gap-2">
        <DesignColorDots summary={item.summary} />
        <span className="type-caption min-w-0 truncate text-on-surface-variant">
          {designFontsText(item.summary)}
          {item.summary.withLogos ? " · с логотипами" : ""}
        </span>
      </div>
      {item.matchesDraft || item.matchesPublished ? (
        <div className="flex flex-wrap gap-2">
          {item.matchesDraft ? <StatusPill tone="progress">В работе</StatusPill> : null}
          {item.matchesPublished ? <StatusPill tone="success">На сайте</StatusPill> : null}
        </div>
      ) : null}
      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onPreview}>
            Посмотреть
          </Button>
          <Button variant="outline" disabled={item.matchesDraft} onClick={onApply}>
            {item.matchesDraft ? "Уже в черновике" : "Применить ко всему сайту"}
          </Button>
        </div>
      ) : null}
      {canEdit && blocker ? <p className="type-caption text-on-surface-variant">{blocker}</p> : null}
    </article>
  );
}
